import { useEffect, useRef, useCallback, useState } from 'react';

const API_BASE = 'https://api.growl.games';
const POLL_INTERVAL_MS = 10_000;

export interface AdminNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  timestamp: string;
  read: boolean;
  data: any;
  db_id?: number;
}

type EventType =
  | 'crypto_deposit'
  | 'inr_deposit_new'
  | 'inr_deposit_status'
  | 'withdrawal_new'
  | 'withdrawal_status';

function buildNotificationContent(
  type: EventType,
  data: any
): { title: string; body: string } {
  switch (type) {
    case 'crypto_deposit': {
      const statusEmoji: Record<string, string> = {
        Success: '✅',
        Processing: '⏳',
        Failed: '❌',
        Rejected: '🚫',
      };
      const emoji = statusEmoji[data.status] ?? '🔔';
      return {
        title: `${emoji} Crypto Deposit — ${data.status}`,
        body: `${data.coinSymbol ?? 'Crypto'} | Order: ${data.orderId}`,
      };
    }
    case 'inr_deposit_new':
      return {
        title: '💰 New INR Deposit Request',
        body: `User #${data.user_id} — ₹${data.amount}${data.provider ? ` via ${data.provider}` : ''}`,
      };
    case 'inr_deposit_status': {
      const emoji = data.status === 'approved' ? '✅' : '❌';
      return {
        title: `${emoji} INR Deposit ${data.status === 'approved' ? 'Approved' : 'Rejected'}`,
        body: `Deposit #${data.id}${data.amount ? ` — ₹${data.amount}` : ''}`,
      };
    }
    case 'withdrawal_new':
      return {
        title: '📤 New Withdrawal Request',
        body: `User #${data.uid} — ${data.currency} ${data.amount}`,
      };
    case 'withdrawal_status': {
      const emoji = data.status === 'Approved' ? '✅' : data.status === 'Rejected' ? '❌' : '🔄';
      return {
        title: `${emoji} Withdrawal ${data.status}`,
        body: `Withdrawal #${data.id} — ${data.currency} ${data.amount}`,
      };
    }
    default:
      return { title: '🔔 Admin Alert', body: data.message || JSON.stringify(data) };
  }
}

function showBrowserNotification(title: string, body: string) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    new Notification(title, { body, icon: '/favicon.png', badge: '/favicon.png', silent: false });
  } catch (_) {}
}

export function useAdminNotifications() {
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const pollRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const shownDbIds = useRef<Set<number>>(new Set());

  const fetchUnread = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(
        `${API_BASE}/api/admin/notifications/unread?token=${encodeURIComponent(token)}`
      );
      if (!res.ok) return;

      const { notifications: rows } = await res.json() as { notifications: any[] };
      if (!rows?.length) return;

      const fresh: AdminNotification[] = [];
      for (const row of rows) {
        if (shownDbIds.current.has(row.id)) continue;
        shownDbIds.current.add(row.id);

        const { title, body } = buildNotificationContent(row.type as EventType, row.data);
        fresh.push({
          id: `${row.id}-${Math.random().toString(36).slice(2)}`,
          type: row.type,
          title,
          body,
          timestamp: row.created_at,
          read: false,
          data: row.data,
          db_id: row.id,
        });
      }

      if (fresh.length === 0) return;

      setNotifications(prev => [...fresh, ...prev].slice(0, 50));
      setUnreadCount(c => c + fresh.length);
      for (const n of fresh) showBrowserNotification(n.title, n.body);
      console.log(`[AdminNotif] Fetched ${fresh.length} new notification(s)`);
    } catch (e) {
      console.error('[AdminNotif] Poll failed:', e);
    }
  }, []);

  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().then(p =>
        console.log('[AdminNotif] Notification permission:', p)
      );
    }

    fetchUnread();
    pollRef.current = setInterval(fetchUnread, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchUnread]);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API_BASE}/api/admin/notifications/mark-read?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      }).catch(() => {});
    }
  }, []);

  const markRead = useCallback((id: string, db_id?: number) => {
    setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount(c => Math.max(0, c - 1));
    if (db_id != null) {
      const token = localStorage.getItem('token');
      if (token) {
        fetch(`${API_BASE}/api/admin/notifications/mark-read?token=${encodeURIComponent(token)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: [db_id] }),
        }).catch(() => {});
      }
    }
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  return { notifications, unreadCount, markAllRead, markRead, clearAll };
}
